import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { Send } from "lucide-react";

/**
 * CreateRequest — Buyer requests a custom item from a specific Jastiper.
 *
 * Flow (Iteration 5 / Iteration 4 backend):
 *   Buyer fills item details → submit (no price) → Jastiper quotes price → Buyer accepts → escrow order.
 *
 * The Jastiper target must be specified. We accept either:
 *   - Query params: ?jastiper_id=...&trip_id=...  (preferred — when buyer clicks
 *     "Request Custom Item" from a TripDetail page)
 *   - Otherwise we let the buyer pick from currently-active Jastipers (their trips).
 */
export default function CreateRequest() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryJastiper = params.get("jastiper_id") || "";
  const queryTrip = params.get("trip_id") || "";

  const [countries, setCountries] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    item_name: "",
    description: "",
    source_country: "",
    photo_path: null,
    deadline: "",
    target_jastiper_id: queryJastiper,
    target_trip_id: queryTrip,
  });

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
    // Active trips list — drives the Jastiper dropdown
    api.get("/trips?status=active").then((r) => setTrips(r.data));
  }, []);

  // When buyer picks a trip OR when trips array first loads, sync jastiper + country.
  useEffect(() => {
    if (trips.length === 0) return;
    setForm((f) => {
      // Always honour URL-preselected jastiper if we have one and it exists in the trips list
      const next = { ...f };
      if (next.target_trip_id) {
        const t = trips.find((x) => x.id === next.target_trip_id);
        if (t) {
          next.target_jastiper_id = t.jastiper_id;
          if (!next.source_country) next.source_country = t.destination_country;
        }
      } else if (queryJastiper) {
        // No trip preselected but jastiper was — make sure the select reflects it
        next.target_jastiper_id = queryJastiper;
      }
      return next;
    });
  }, [form.target_trip_id, trips, queryJastiper]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_name || !form.description || !form.source_country) {
      toast.error("Mohon isi nama item, deskripsi, dan negara asal");
      return;
    }
    if (!form.target_jastiper_id) {
      toast.error("Pilih Jastiper tujuan dulu");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        item_name: form.item_name,
        description: form.description,
        source_country: form.source_country,
        photo_path: form.photo_path,
        deadline: form.deadline || null,
        target_jastiper_id: form.target_jastiper_id,
        target_trip_id: form.target_trip_id || null,
      };
      const r = await api.post("/requests", payload);
      toast.success("Permintaan terkirim! Menunggu Jastiper memberi harga.");
      navigate(`/requests/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Gagal mengirim permintaan");
    } finally {
      setLoading(false);
    }
  };

  // Distinct list of active Jastipers (de-dupe across multiple trips).
  const jastiperOptions = Array.from(
    trips.reduce((map, t) => {
      if (!map.has(t.jastiper_id)) {
        map.set(t.jastiper_id, {
          id: t.jastiper_id,
          name: t.jastiper_name,
          destination: t.destination_country,
        });
      }
      return map;
    }, new Map()).values()
  );

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="create-request-page">
      <div>
        <div className="label-caps mb-2">Permintaan Baru</div>
        <h1 className="font-display text-4xl tracking-tight">Pesan dari Jastiper.</h1>
        <p className="mt-2" style={{color: "var(--app-muted)"}}>
          Pilih Jastiper yang sedang travel, jelaskan barangnya. Jastiper akan memberi penawaran harga.
        </p>
      </div>

      <form onSubmit={submit} className="surface p-8 space-y-5">
        <div>
          <label className="label-caps mb-2 block">Trip Tujuan *</label>
          <select
            className="input-flat"
            value={form.target_trip_id}
            onChange={(e) => set("target_trip_id", e.target.value)}
            data-testid="request-trip-select"
          >
            <option value="">— Pilih trip Jastiper —</option>
            {trips.map((t) => (
              <option key={t.id} value={t.id}>
                {t.jastiper_name} → {t.destination_country}{t.destination_city ? `, ${t.destination_city}` : ""}
              </option>
            ))}
          </select>
          <p className="text-xs mt-2" style={{color: "var(--app-muted)"}}>
            Atau pilih Jastiper langsung di bawah ini.
          </p>
        </div>

        <div>
          <label className="label-caps mb-2 block">Atau pilih Jastiper *</label>
          <select
            className="input-flat"
            value={form.target_jastiper_id}
            onChange={(e) => set("target_jastiper_id", e.target.value)}
            data-testid="request-jastiper-select"
          >
            <option value="">— Pilih Jastiper —</option>
            {jastiperOptions.map((j) => (
              <option key={j.id} value={j.id}>{j.name} (ke {j.destination})</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label-caps mb-2 block">Nama Item *</label>
          <input
            className="input-flat"
            value={form.item_name}
            onChange={(e) => set("item_name", e.target.value)}
            placeholder="cth. Nintendo Switch OLED"
            data-testid="request-name-input"
          />
        </div>

        <div>
          <label className="label-caps mb-2 block">Deskripsi *</label>
          <textarea
            className="input-flat min-h-[120px]"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Brand, model, warna, ukuran — sespecifik mungkin"
            data-testid="request-desc-input"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-caps mb-2 block">Negara Asal *</label>
            <select
              className="input-flat"
              value={form.source_country}
              onChange={(e) => set("source_country", e.target.value)}
              data-testid="request-country-input"
            >
              <option value="">Pilih negara</option>
              {countries.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps mb-2 block">Tenggat (opsional)</label>
            <input
              type="date"
              className="input-flat"
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
              data-testid="request-deadline-input"
            />
          </div>
        </div>

        <div>
          <label className="label-caps mb-2 block">Foto Referensi</label>
          <ImageUploader value={form.photo_path} onChange={(p) => set("photo_path", p)} testid="request-photo" />
        </div>

        <div className="border-t pt-5 flex justify-end" style={{borderColor: "var(--app-border)"}}>
          <button type="submit" className="btn-primary-flat" disabled={loading} data-testid="submit-request-btn">
            {loading ? "Mengirim..." : "Kirim Permintaan"}
            <Send size={16} strokeWidth={1.5} />
          </button>
        </div>

        <p className="text-xs" style={{color: "var(--app-muted)"}}>
          💡 Harga dan biaya ditentukan oleh Jastiper setelah menerima permintaan ini.
        </p>
      </form>
    </div>
  );
}
